(function () {
  const FALLBACK_LOCATION = '[INSERT CONFIRMED LOCATION]';
  const STORAGE_KEY = 'the-spot-employment-application';

  class EmploymentApplication extends HTMLElement {
    constructor() {
      super();
      this.currentStep = 'position';
      this.positions = [];
      this.selectedPosition = null;
      this.qualificationResult = '';
      this.lastFocusedControl = null;
    }

    connectedCallback() {
      this.positions = this.readJson('[data-employment-positions]', []);
      this.settings = this.readJson('[data-employment-settings]', {});
      this.roleConfigs = this.buildRoleConfigs();
      this.interactive = this.querySelector('[data-employment-interactive]');
      this.fallback = this.querySelector('[data-employment-fallback]');
      this.form = this.querySelector('#EmploymentApplicationForm');
      this.formState = this.querySelector('[data-employment-state]');
      this.status = this.querySelector('[data-employment-status]');
      this.steps = Array.from(this.querySelectorAll('[data-employment-step]'));
      this.positionSelect = this.querySelector('[data-employment-position-select]');
      this.hiddenFields = this.querySelector('[data-employment-hidden-fields]');
      this.prescreenContainer = this.querySelector('[data-employment-prescreen-questions]');
      this.overviewCard = this.querySelector('[data-employment-overview-card]');
      this.roleDetailsContainer = this.querySelector('[data-employment-role-details]');
      this.experienceContainer = this.querySelector('[data-employment-experience-options]');
      this.roleExperienceSection = this.querySelector('[data-role-experience-section]');
      this.errorSummary = this.querySelector('[data-employment-error-summary]');
      this.submitButton = this.querySelector('[data-employment-submit]');

      if (!this.interactive || !this.form || !this.positions.length) return;

      this.form.setAttribute('novalidate', 'novalidate');
      this.fallback?.setAttribute('hidden', '');
      this.interactive.removeAttribute('hidden');
      this.bindEvents();
      this.restoreFailedSubmission();

      if (this.formState?.dataset.formSuccess === 'true') {
        this.showSuccessFromStorage();
      } else {
        this.showStep('position', { announce: false, focus: false });
      }
    }

    readJson(selector, fallback) {
      const script = this.querySelector(selector);
      if (!script) return fallback;

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('Employment application JSON could not be parsed.', error);
        return fallback;
      }
    }

    buildRoleConfigs() {
      const configuredHandle = this.normalizeHandle(this.settings.fulfillmentArticleHandle);
      const fulfillmentHandle = configuredHandle || 'ecommerce-order-fulfillment-associate';
      const fulfillmentLocation = (this.settings.fulfillmentLocation || '').trim() || FALLBACK_LOCATION;

      return {
        [fulfillmentHandle]: {
          label: 'eCommerce Order Fulfillment Associate',
          details: [
            'In-person position',
            '$20.00 per hour',
            'Monday through Friday, 10:00 a.m.-5:00 p.m.',
            'Saturdays, 10:00 a.m.-3:00 p.m., as needed',
            'Active picking, packing, and inventory work',
            'Regular use of computers and order-management systems'
          ],
          questions: [
            {
              name: 'age_18_or_older',
              order: 1,
              text: 'Are you at least 18 years old?',
              type: 'radio',
              options: ['Yes', 'No'],
              disqualifying: true,
              qualifies: ['Yes']
            },
            {
              name: 'authorized_to_work_us',
              order: 2,
              text: 'Are you legally authorized to work in the United States?',
              type: 'radio',
              options: ['Yes', 'No'],
              disqualifying: true,
              qualifies: ['Yes']
            },
            {
              name: 'reliable_location_attendance',
              order: 3,
              text: `This position must be performed in person at our fulfillment location in ${fulfillmentLocation}. Can you reliably report to this location for each scheduled shift?`,
              type: 'radio',
              options: ['Yes', 'No'],
              disqualifying: true,
              qualifies: ['Yes']
            },
            {
              name: 'weekday_availability',
              order: 4,
              text: 'Are you available to work Monday through Friday from 10:00 a.m. to 5:00 p.m.?',
              type: 'radio',
              options: ['Yes', 'No'],
              disqualifying: true,
              qualifies: ['Yes']
            },
            {
              name: 'saturday_availability',
              order: 5,
              text: 'Are you available to work Saturdays from 10:00 a.m. to 3:00 p.m. when needed?',
              type: 'radio',
              options: ['Yes', 'No', 'It depends on how much advance notice is provided'],
              disqualifying: false,
              qualifies: ['Yes', 'No', 'It depends on how much advance notice is provided']
            },
            {
              name: 'essential_physical_responsibilities',
              order: 6,
              text: 'This position requires remaining active and standing for periods of up to four hours, as well as safely lifting and carrying items weighing up to 50 pounds. Are you able to perform these essential job responsibilities, with or without reasonable accommodation?',
              type: 'radio',
              options: ['Yes', 'No'],
              disqualifying: true,
              qualifies: ['Yes']
            },
            {
              name: 'computer_programs',
              order: 7,
              text: 'This position requires using computers and learning new order-processing, shipping, and inventory programs. Are you willing and able to use computer programs throughout the workday?',
              type: 'radio',
              options: ['Yes', 'No'],
              disqualifying: true,
              qualifies: ['Yes']
            }
          ],
          experienceOptions: [
            'Picking and packing customer orders',
            'Order fulfillment',
            'Inventory management',
            'Amazon Seller Central',
            'Shopify',
            'eBay',
            'Another eCommerce or online marketplace platform',
            'None of the above'
          ]
        }
      };
    }

    normalizeHandle(value) {
      return String(value || '').trim().split('/').filter(Boolean).pop() || '';
    }

    bindEvents() {
      this.querySelectorAll('[data-employment-action]').forEach((button) => {
        button.addEventListener('click', this.handleAction.bind(this));
      });

      this.positionSelect?.addEventListener('change', () => {
        this.clearStepErrors(this.querySelector('[data-employment-step="position"]'));
        this.resetRoleSpecificAnswers();
        this.updateSelectedPosition();
      });

      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      this.form.addEventListener('change', (event) => {
        if (event.target.matches('[data-none-option]')) {
          this.handleExclusiveNoneOption(event.target);
        }
        this.clearFieldError(event.target.closest('[data-employment-field], [data-employment-fieldset]'));
      });

      this.form.addEventListener('input', (event) => {
        this.clearFieldError(event.target.closest('[data-employment-field], [data-employment-fieldset]'));
      });
    }

    handleAction(event) {
      const action = event.currentTarget.dataset.employmentAction;
      this.lastFocusedControl = event.currentTarget;

      if (action === 'position-continue') {
        if (!this.validatePositionStep()) return;
        this.prepareOverview();
        this.showStep('overview');
        return;
      }

      if (action === 'begin-prescreening') {
        this.preparePrescreening();
        if (this.getCurrentConfig()?.questions?.length) {
          this.showStep('prescreen');
        } else {
          this.qualificationResult = 'Qualified - no role-specific prescreening configured';
          this.prepareQualification(true);
          this.showStep('qualified');
        }
        return;
      }

      if (action === 'evaluate-prescreening') {
        if (!this.validatePrescreening()) return;
        const isQualified = this.evaluatePrescreening();
        this.qualificationResult = isQualified ? 'Qualified' : 'Not qualified';
        this.prepareQualification(isQualified);
        this.showStep(isQualified ? 'qualified' : 'not-qualified');
        return;
      }

      if (action === 'continue-application') {
        this.prepareApplication();
        this.showStep('application');
        return;
      }

      if (action === 'back') {
        this.showStep(event.currentTarget.dataset.targetStep || 'position');
        return;
      }

      if (action === 'view-other') {
        this.resetPositionAndPrescreening();
        this.showStep('position');
      }
    }

    validatePositionStep() {
      if (this.positionSelect?.value) return true;
      this.showFieldError(
        this.positionSelect,
        'Please select a position before continuing.'
      );
      this.announce('Please select a position before continuing.');
      this.positionSelect?.focus();
      return false;
    }

    updateSelectedPosition() {
      const selectedHandle = this.normalizeHandle(this.positionSelect?.value);
      this.selectedPosition = this.positions.find((position) => {
        return this.normalizeHandle(position.handle) === selectedHandle;
      }) || null;
    }

    prepareOverview() {
      this.updateSelectedPosition();
      if (!this.selectedPosition) return;

      this.setText('[data-selected-position-title]', this.selectedPosition.title);
      this.querySelectorAll('[data-selected-position-link]').forEach((link) => {
        link.href = this.selectedPosition.url;
      });

      const excerpt = this.querySelector('[data-selected-position-excerpt]');
      if (excerpt) {
        excerpt.textContent = this.selectedPosition.excerpt || '';
        excerpt.toggleAttribute('hidden', !this.selectedPosition.excerpt);
      }

      const config = this.getCurrentConfig();
      this.roleDetailsContainer.replaceChildren();
      if (config?.details?.length) {
        const list = document.createElement('ul');
        list.className = 'employment-application__details';
        config.details.forEach((detail) => {
          const item = document.createElement('li');
          item.textContent = detail;
          list.append(item);
        });
        this.roleDetailsContainer.append(list);
        this.overviewCard?.removeAttribute('hidden');
      } else {
        this.overviewCard?.setAttribute('hidden', '');
      }
    }

    preparePrescreening() {
      const config = this.getCurrentConfig();
      this.prescreenContainer.replaceChildren();

      if (!config?.questions?.length) {
        const note = document.createElement('p');
        note.className = 'employment-application__no-questions';
        note.textContent = 'This role does not currently have role-specific prescreening questions configured. You can continue to the general application.';
        this.prescreenContainer.append(note);
        return;
      }

      config.questions
        .slice()
        .sort((a, b) => a.order - b.order)
        .forEach((question) => {
          const fieldset = document.createElement('fieldset');
          fieldset.className = 'employment-application__fieldset';
          fieldset.dataset.employmentFieldset = question.name;
          fieldset.dataset.questionText = question.text;

          const legend = document.createElement('legend');
          legend.className = 'employment-application__legend';
          legend.textContent = question.text;
          fieldset.append(legend);

          const optionList = document.createElement('div');
          optionList.className = 'employment-application__option-list';

          question.options.forEach((option, index) => {
            const optionId = `employment-${question.name}-${index}`;
            const label = document.createElement('label');
            label.className = 'employment-application__option';
            label.htmlFor = optionId;

            const input = document.createElement('input');
            input.type = 'radio';
            input.id = optionId;
            input.name = `prescreen_${question.name}`;
            input.value = option;
            input.required = true;
            input.dataset.prescreenName = question.name;

            const text = document.createElement('span');
            text.textContent = option;

            label.append(input, text);
            optionList.append(label);
          });

          fieldset.append(optionList);
          this.prescreenContainer.append(fieldset);
        });
    }

    validatePrescreening() {
      const config = this.getCurrentConfig();
      if (!config?.questions?.length) return true;

      const errors = [];
      config.questions.forEach((question) => {
        const checked = this.querySelector(`input[name="prescreen_${question.name}"]:checked`);
        if (!checked) {
          const fieldset = this.querySelector(`[data-employment-fieldset="${question.name}"]`);
          this.showFieldError(fieldset, 'Please choose an answer.');
          errors.push(question.text);
        }
      });

      if (errors.length) {
        this.announce('Please answer every prescreening question before continuing.');
        const firstInvalid = this.querySelector('.employment-application__is-invalid input');
        firstInvalid?.focus();
        return false;
      }

      return true;
    }

    evaluatePrescreening() {
      const config = this.getCurrentConfig();
      if (!config?.questions?.length) return true;

      return config.questions.every((question) => {
        if (!question.disqualifying) return true;
        const checked = this.querySelector(`input[name="prescreen_${question.name}"]:checked`);
        return checked && question.qualifies.includes(checked.value);
      });
    }

    prepareQualification(isQualified) {
      this.setText('[data-qualified-position]', this.selectedPosition?.title || '');
      this.setText('[data-not-qualified-position]', this.selectedPosition?.title || '');
      this.announce(isQualified ? 'You meet the requirements for this role.' : 'This role does not appear to be the right match at this time.');
    }

    prepareApplication() {
      this.setText('[data-application-position]', this.selectedPosition?.title || '');
      this.renderExperienceOptions();
      this.updateHiddenFields();
    }

    renderExperienceOptions() {
      const config = this.getCurrentConfig();
      this.experienceContainer.replaceChildren();

      if (!config?.experienceOptions?.length) {
        this.roleExperienceSection?.setAttribute('hidden', '');
        return;
      }

      this.roleExperienceSection?.removeAttribute('hidden');

      config.experienceOptions.forEach((option, index) => {
        const id = `employment-experience-${index}`;
        const label = document.createElement('label');
        label.className = 'employment-application__option';
        label.htmlFor = id;

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = id;
        input.name = `contact[Experience - ${option}]`;
        input.value = 'Yes';
        if (option === 'None of the above') {
          input.dataset.noneOption = 'true';
        }

        const text = document.createElement('span');
        text.textContent = option;
        label.append(input, text);
        this.experienceContainer.append(label);
      });
    }

    handleExclusiveNoneOption(changedInput) {
      const options = Array.from(this.experienceContainer.querySelectorAll('input[type="checkbox"]'));
      if (changedInput.dataset.noneOption === 'true' && changedInput.checked) {
        options.forEach((input) => {
          if (input !== changedInput) input.checked = false;
        });
        return;
      }

      if (changedInput.checked) {
        const none = this.experienceContainer.querySelector('[data-none-option]');
        if (none) none.checked = false;
      }
    }

    handleSubmit(event) {
      this.clearApplicationErrors();

      if (!this.validateApplication()) {
        event.preventDefault();
        return;
      }

      this.updateHiddenFields();
      this.saveSubmissionState();

      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.textContent = 'Submitting...';
      }
    }

    validateApplication() {
      const applicationStep = this.querySelector('[data-employment-step="application"]');
      const fields = Array.from(applicationStep.querySelectorAll('input, textarea, select'));
      const errors = [];

      fields.forEach((field) => {
        if (field.disabled || field.type === 'hidden') return;

        if (field.type === 'radio') return;
        if (field.type === 'checkbox' && !field.required) return;

        if (!field.checkValidity()) {
          const label = this.getFieldLabel(field);
          const message = field.validity.typeMismatch
            ? 'Please enter a valid value.'
            : 'Please complete this required field.';
          this.showFieldError(field, message);
          errors.push(label);
        }
      });

      applicationStep.querySelectorAll('[data-required-radio-group]').forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) {
          this.showFieldError(fieldset, 'Please choose an answer.');
          errors.push(this.getFieldLabel(fieldset));
        }
      });

      if (errors.length) {
        this.renderErrorSummary(errors);
        this.announce('Please correct the errors in the application before submitting.');
        const firstInvalid = applicationStep.querySelector('.employment-application__is-invalid input, .employment-application__is-invalid textarea, .employment-application__is-invalid select');
        firstInvalid?.focus();
        return false;
      }

      return true;
    }

    updateHiddenFields() {
      if (!this.hiddenFields) return;

      this.hiddenFields.replaceChildren();
      const selected = this.selectedPosition || this.getSelectedFromFormState();
      const prescreenResponses = this.getPrescreenResponses();
      const saturdayResponse = prescreenResponses.find((response) => response.name === 'saturday_availability');

      this.addHidden('Selected position title', selected?.title || '');
      this.addHidden('Careers article handle', selected?.handle || '');
      this.addHidden('Careers article URL', selected?.url || '');
      this.addHidden('Qualification result', this.qualificationResult || 'Qualified');
      this.addHidden('Saturday availability response', saturdayResponse?.answer || '');
      this.addHidden('Submission date', new Date().toISOString());
      this.addHidden('Source', 'The Spot Chicago Careers Application');
      this.addHidden('subject', `Employment Application: ${selected?.title || 'Selected Position'}`);

      prescreenResponses.forEach((response) => {
        this.addHidden(`Prescreen - ${response.question}`, response.answer);
      });

      this.addHidden('body', this.buildSummaryBody(selected, prescreenResponses));
    }

    addHidden(label, value) {
      const input = document.createElement(label === 'body' ? 'textarea' : 'input');
      if (input.tagName === 'INPUT') input.type = 'hidden';
      input.name = `contact[${label}]`;
      input.value = value || '';
      this.hiddenFields.append(input);
    }

    getPrescreenResponses() {
      const config = this.getCurrentConfig();
      if (!config?.questions?.length) return [];

      return config.questions
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((question) => {
          const checked = this.querySelector(`input[name="prescreen_${question.name}"]:checked`);
          return {
            name: question.name,
            question: question.text,
            answer: checked?.value || ''
          };
        });
    }

    buildSummaryBody(selected, prescreenResponses) {
      const lines = [
        'The Spot Chicago Careers Application',
        '',
        `Selected position: ${selected?.title || ''}`,
        `Careers article handle: ${selected?.handle || ''}`,
        `Qualification result: ${this.qualificationResult || 'Qualified'}`,
        ''
      ];

      if (prescreenResponses.length) {
        lines.push('Prescreening responses:');
        prescreenResponses.forEach((response) => {
          lines.push(`- ${response.question}: ${response.answer}`);
        });
        lines.push('');
      }

      lines.push('Application details are included in the individually named fields submitted with this message.');
      return lines.join('\n');
    }

    addStoredValues(values) {
      Object.entries(values || {}).forEach(([name, value]) => {
        const fields = Array.from(this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`));
        if (!fields.length) return;

        fields.forEach((field) => {
          if (field.type === 'checkbox' || field.type === 'radio') {
            field.checked = Array.isArray(value) ? value.includes(field.value) : field.value === value;
          } else {
            field.value = value;
          }
        });
      });
    }

    saveSubmissionState() {
      const data = new FormData(this.form);
      const values = {};

      data.forEach((value, name) => {
        if (values[name]) {
          values[name] = Array.isArray(values[name]) ? values[name].concat(value) : [values[name], value];
        } else {
          values[name] = value;
        }
      });

      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        position: this.selectedPosition,
        qualificationResult: this.qualificationResult,
        values
      }));
    }

    restoreFailedSubmission() {
      if (this.formState?.dataset.formErrors !== 'true') return;

      try {
        const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
        if (stored.position?.handle && this.positionSelect) {
          this.positionSelect.value = this.normalizeHandle(stored.position.handle);
          this.updateSelectedPosition();
          this.prepareApplication();
          this.addStoredValues(stored.values);
          this.qualificationResult = stored.qualificationResult || 'Qualified';
          this.showStep('application', { announce: true, focus: false });
        }
      } catch (error) {
        console.warn('Employment application values could not be restored.', error);
      }
    }

    showSuccessFromStorage() {
      try {
        const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
        const title = stored.position?.title || 'the selected position';
        this.setText('[data-success-position]', title);
      } catch (error) {
        this.setText('[data-success-position]', 'the selected position');
      }

      window.sessionStorage.removeItem(STORAGE_KEY);
      this.showStep('success', { announce: true, focus: true });
    }

    getSelectedFromFormState() {
      if (this.selectedPosition) return this.selectedPosition;
      if (!this.positionSelect?.value) return null;
      const selectedHandle = this.normalizeHandle(this.positionSelect.value);
      return this.positions.find((position) => this.normalizeHandle(position.handle) === selectedHandle) || null;
    }

    getCurrentConfig() {
      const selected = this.selectedPosition || this.getSelectedFromFormState();
      const handle = this.normalizeHandle(selected?.handle);
      return handle ? this.roleConfigs[handle] : null;
    }

    resetRoleSpecificAnswers() {
      this.prescreenContainer?.querySelectorAll('input').forEach((input) => {
        input.checked = false;
      });
      this.experienceContainer?.replaceChildren();
      this.roleExperienceSection?.setAttribute('hidden', '');
      this.qualificationResult = '';
    }

    resetPositionAndPrescreening() {
      if (this.positionSelect) this.positionSelect.value = '';
      this.selectedPosition = null;
      this.resetRoleSpecificAnswers();
      this.clearApplicationErrors();
      this.hiddenFields?.replaceChildren();
    }

    showStep(stepName, options = {}) {
      const shouldAnnounce = options.announce !== false;
      const shouldFocus = options.focus !== false;
      this.currentStep = stepName;

      this.steps.forEach((step) => {
        step.toggleAttribute('hidden', step.dataset.employmentStep !== stepName);
      });

      if (shouldAnnounce) {
        const heading = this.querySelector(`[data-employment-step="${stepName}"] [data-step-heading]`);
        this.announce(heading?.textContent?.trim() || 'Application step changed.');
      }

      if (shouldFocus) {
        window.requestAnimationFrame(() => {
          const heading = this.querySelector(`[data-employment-step="${stepName}"] [data-step-heading]`);
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            heading.focus({ preventScroll: false });
          }
        });
      }
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => {
        element.textContent = value;
      });
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.requestAnimationFrame(() => {
        this.status.textContent = message;
      });
    }

    getFieldLabel(field) {
      if (!field) return 'This field';
      if (field.matches('fieldset')) {
        return field.querySelector('legend')?.textContent?.replace('*', '').trim() || 'This question';
      }

      const explicitLabel = this.querySelector(`label[for="${CSS.escape(field.id)}"]`);
      return explicitLabel?.textContent?.replace('*', '').trim() || field.placeholder || field.name || 'This field';
    }

    showFieldError(field, message) {
      if (!field) return;

      const wrapper = field.matches('[data-employment-field], [data-employment-fieldset]')
        ? field
        : field.closest('[data-employment-field], [data-employment-fieldset]');

      if (!wrapper) return;

      wrapper.classList.add('employment-application__is-invalid');
      let error = wrapper.querySelector('[data-field-error]');
      if (!error) {
        error = document.createElement('span');
        error.className = 'employment-application__field-error';
        error.dataset.fieldError = 'true';
        wrapper.append(error);
      }

      const errorId = `${field.id || wrapper.dataset.employmentFieldset || 'employment-field'}-error`;
      error.id = errorId;
      error.textContent = message;

      const target = field.matches('fieldset') ? wrapper.querySelector('input, select, textarea') : field;
      if (target) {
        const describedBy = new Set((target.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        describedBy.add(errorId);
        target.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
        target.setAttribute('aria-invalid', 'true');
      }
    }

    clearFieldError(wrapper) {
      if (!wrapper) return;
      wrapper.classList.remove('employment-application__is-invalid');
      wrapper.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
        field.removeAttribute('aria-invalid');
      });
      wrapper.querySelector('[data-field-error]')?.remove();
    }

    clearStepErrors(step) {
      step?.querySelectorAll('[data-employment-field], [data-employment-fieldset]').forEach((wrapper) => {
        this.clearFieldError(wrapper);
      });
    }

    clearApplicationErrors() {
      this.clearStepErrors(this.querySelector('[data-employment-step="application"]'));
      if (this.errorSummary) this.errorSummary.replaceChildren();
    }

    renderErrorSummary(errors) {
      if (!this.errorSummary) return;
      this.errorSummary.replaceChildren();

      const heading = document.createElement('h3');
      heading.textContent = 'Please review the following fields';
      const list = document.createElement('ul');
      errors.forEach((label) => {
        const item = document.createElement('li');
        item.textContent = label;
        list.append(item);
      });
      this.errorSummary.append(heading, list);
    }
  }

  if (!customElements.get('employment-application')) {
    customElements.define('employment-application', EmploymentApplication);
  }
})();
